import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalPlatform, ExternalAccount } from '../../types';
import { ConnectAccountModal } from '../../components/ConnectAccountModal';
import { AddCardModal } from '../../components/AddCardModal';
import { useTranslation } from '../../i18n/TranslationContext';
import { useCanSeeCrowdVoltConnect } from '../../access/useFeatureAccess';
import { useSeatSession } from '../useSeatSession';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../services/analytics';
import { SyncFailedError, isRetryableSyncError, syncErrorMessage } from '../../api/syncErrors';
import {
  useAccounts,
  useAccountGroups,
  useUpdateAccountGroupMembers,
  useCrowdVoltStatus,
  useConnectDice,
  useConnectTAO,
  useDisconnectAccount,
  useSyncAccount,
  useSyncAllAccounts,
} from '../../hooks/useApi';
import { Button } from '../../components/base-components';
import { accountPhone } from '../../components/accountIdentity';
import { AccountGroupChip } from '../components/AccountGroupChip';
import { ManageGroupsModal } from '../components/ManageGroupsModal';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Badge } from '../components/Badge';
import { SegmentedControl, SegmentOption } from '../components/SegmentedControl';
import { CloseIcon, SearchIcon } from '../components/icons';
import { EmptyState } from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { cx, CARD_SURFACE, FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';

const ALL_PLATFORMS: ExternalPlatform[] = ['DICE', 'AXS', 'TAO'];

// Platform colors and icons
const platformConfig: Record<string, { color: string; bgColor: string; borderColor: string }> = {
  DICE: { color: 'text-rose-400', bgColor: 'bg-rose-500/10', borderColor: 'border-rose-500/20' },
  AXS: { color: 'text-violet-400', bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/20' },
  TAO: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
};

const defaultPlatformConfig = { color: 'text-white/70', bgColor: 'bg-white/10', borderColor: 'border-white/20' };

type SortMode = 'status' | 'platform' | 'phone' | 'stale' | 'newest';
type StatusFilter = 'all' | 'banned' | 'attention' | 'healthy';

const SORT_MODES: SortMode[] = ['status', 'platform', 'phone', 'stale', 'newest'];
const STATUS_FILTERS: StatusFilter[] = ['all', 'banned', 'attention', 'healthy'];

const SORT_LABEL: Record<SortMode, string> = {
  status: 'Problems first',
  platform: 'Platform',
  phone: 'Number',
  stale: 'Least recently synced',
  newest: 'Newest',
};

const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'All',
  banned: 'Banned',
  attention: 'Attention',
  healthy: 'Healthy',
};

type Bucket = 'banned' | 'attention' | 'healthy';

/**
 * Banned leads: it is the failure that cannot be fixed from this page — the
 * account has to be replaced — and it is the one that silently drops every
 * waitlist join and transfer until someone notices.
 */
const BUCKET_ORDER: Bucket[] = ['banned', 'attention', 'healthy'];

/**
 * Which bucket an account sits in — one function, so the filter counts, the
 * filter itself and the default sort can never disagree about what counts as a
 * problem. The three are a partition: every account lands in exactly one, and
 * the counts add up to the total.
 *
 * There is deliberately no 'inactive' bucket. The accounts endpoint filters to
 * is_active=True before serializing, so an inactive account never reaches this
 * page and the control would have sat there reading 0 forever.
 *
 * needs_reconnect covers expired and banned alike and is the flag the server
 * keeps honest, so it decides the bucket ahead of anything derived from sync
 * state (see the field's note in types.ts).
 */
const bucketOf = (acct: ExternalAccount): Bucket => {
  if (acct.is_banned) return 'banned';
  if (acct.needs_reconnect || acct.last_sync_status === 'failed') return 'attention';
  return 'healthy';
};

/** Within `attention`: a dead token before a merely failed sync. */
const severityOf = (acct: ExternalAccount): number => {
  if (acct.needs_reconnect) return 0;
  if (acct.last_sync_status === 'failed') return 1;
  return 2;
};

const digitsOnly = (value: string): string => value.replace(/\D+/g, '');

/** Never-synced sorts as the oldest possible sync — it is the staler case. */
const syncedAt = (acct: ExternalAccount): number =>
  acct.last_synced ? new Date(acct.last_synced).getTime() : 0;

/** Numberless accounts sort last rather than leading every list. */
const byNumber = (a: ExternalAccount, b: ExternalAccount): number => {
  const pa = accountPhone(a);
  const pb = accountPhone(b);
  if (!pa && !pb) return 0;
  if (!pa) return 1;
  if (!pb) return -1;
  return pa.localeCompare(pb);
};

/**
 * Typing "603" or "(314) 603" has to find +13146034744, so the digits of the
 * query are matched against the digits of the number as well as the raw text.
 */
const matchesQuery = (acct: ExternalAccount, query: string, queryDigits: string): boolean => {
  if (!query) return true;
  const haystack = [acct.platform, acct.email, acct.phone, acct.phone_masked]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (haystack.includes(query)) return true;
  return queryDigits.length > 0 && digitsOnly(acct.phone || '').includes(queryDigits);
};

const VIEW_PREF_KEY = 'flashtools_v2_accounts_view';

interface ViewPref {
  sort: SortMode;
  filter: StatusFilter;
  platforms: ExternalPlatform[];
}

function readViewPref(): ViewPref {
  const fallback: ViewPref = { sort: 'status', filter: 'all', platforms: [] };
  try {
    const raw = window.localStorage.getItem(VIEW_PREF_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ViewPref>;
    return {
      sort: SORT_MODES.includes(parsed.sort as SortMode) ? (parsed.sort as SortMode) : fallback.sort,
      filter: STATUS_FILTERS.includes(parsed.filter as StatusFilter)
        ? (parsed.filter as StatusFilter)
        : fallback.filter,
      platforms: Array.isArray(parsed.platforms)
        ? parsed.platforms.filter((x): x is ExternalPlatform =>
            ALL_PLATFORMS.includes(x as ExternalPlatform))
        : fallback.platforms,
    };
  } catch {
    // Private mode / blocked storage — the defaults are always correct.
    return fallback;
  }
}

const SyncIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 10a2 2 0 012-2h14a2 2 0 012 2M3 10v8a2 2 0 002 2h14a2 2 0 002-2v-8M7 15h2" />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

/** Icon-only action, `lg`+ only. Resting at 40% (never 0) per DESIGN_PLAN §4.4. */
const iconActionClass = cx(
  'p-2 rounded-lg bg-white/5 transition-all disabled:opacity-50',
  FOCUS_RING,
);

/** Icon + label action, below `lg`, always visible. */
const labelledActionClass = cx(
  'flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5',
  'px-3 py-2 text-xs font-medium transition-all disabled:opacity-50',
  FOCUS_RING,
);

export const AccountsScreen: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const { data: cvStatus } = useCrowdVoltStatus();
  const isCvConnected = cvStatus?.connected === true && !cvStatus?.is_token_expired;
  const canSeeCvConnect = useCanSeeCrowdVoltConnect();
  // A team member is shown this page so they know WHICH account holds a ticket, and
  // so they can refresh it. Connecting, disconnecting, reconnecting and adding
  // a card are the owner's alone — the endpoints refuse a team member, so leaving the
  // buttons up would only produce 403s. Sync stays: it is the whole reason a
  // team member has this page.
  const { isSeat } = useSeatSession();
  const canManageAccounts = !isSeat;

  const {
    data: accounts = [],
    isLoading: loading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useAccounts();

  // The DICE account we're adding a card to (null = modal closed).
  const [addCardAccount, setAddCardAccount] = useState<ExternalAccount | null>(null);

  const [modalPlatform, setModalPlatform] = useState<ExternalPlatform | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // The account being reconnected (null for a fresh connect). Drives prefill /
  // straight-to-OTP behavior in the modal.
  const [reconnectAccount, setReconnectAccount] = useState<ExternalAccount | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [{ sort: sortMode, filter: statusFilter, platforms: platformFilter }, setViewPref] =
    useState<ViewPref>(readViewPref);

  // Account groups: tags the owner puts on accounts. Read-only for a team
  // member — the tags still help them tell accounts apart, but the endpoints
  // that change them refuse a seat. Not persisted with the view prefs: a
  // group can be deleted between visits, and a stale id would hide every row.
  const { data: groups = [] } = useAccountGroups();
  const groupMembers = useUpdateAccountGroupMembers();
  const [groupFilter, setGroupFilter] = useState<number | null>(null);
  const [manageGroups, setManageGroups] = useState<{
    open: boolean;
    groupId: number | null;
    accountId: number | null;
  }>({ open: false, groupId: null, accountId: null });
  const openManageGroups = (groupId: number | null = null, accountId: number | null = null) =>
    setManageGroups({ open: true, groupId, accountId });

  const setSortMode = (sort: SortMode) => setViewPref((prev) => ({ ...prev, sort }));
  const setStatusFilter = (filter: StatusFilter) => setViewPref((prev) => ({ ...prev, filter }));
  const togglePlatform = (platform: ExternalPlatform) =>
    setViewPref((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));

  useEffect(() => {
    try {
      window.localStorage.setItem(
        VIEW_PREF_KEY,
        JSON.stringify({ sort: sortMode, filter: statusFilter, platforms: platformFilter }),
      );
    } catch {
      // Blocked storage is not worth a broken page.
    }
  }, [sortMode, statusFilter, platformFilter]);

  const counts = useMemo(() => {
    const acc: Record<StatusFilter, number> = {
      all: accounts.length, banned: 0, attention: 0, healthy: 0,
    };
    accounts.forEach((a) => { acc[bucketOf(a)] += 1; });
    return acc;
  }, [accounts]);

  /** Only offer a platform chip for a platform the owner actually has. */
  const platformChips = useMemo(
    () => ALL_PLATFORMS.filter((p) => accounts.some((a) => a.platform === p)),
    [accounts],
  );

  const visibleAccounts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const queryDigits = digitsOnly(query);
    const filtered = accounts.filter((a) => {
      if (platformFilter.length > 0 && !platformFilter.includes(a.platform)) return false;
      if (statusFilter !== 'all' && bucketOf(a) !== statusFilter) return false;
      if (groupFilter != null && !(a.groups ?? []).some((g) => g.id === groupFilter)) return false;
      return matchesQuery(a, query, queryDigits);
    });

    const sorted = [...filtered];
    if (sortMode === 'platform') {
      sorted.sort((a, b) => a.platform.localeCompare(b.platform) || byNumber(a, b));
    } else if (sortMode === 'phone') {
      sorted.sort(byNumber);
    } else if (sortMode === 'stale') {
      sorted.sort((a, b) => syncedAt(a) - syncedAt(b) || byNumber(a, b));
    } else if (sortMode === 'newest') {
      sorted.sort((a, b) =>
        new Date(b.connected_at).getTime() - new Date(a.connected_at).getTime());
    } else {
      sorted.sort((a, b) =>
        BUCKET_ORDER.indexOf(bucketOf(a)) - BUCKET_ORDER.indexOf(bucketOf(b)) ||
        severityOf(a) - severityOf(b) ||
        byNumber(a, b));
    }
    return sorted;
  }, [accounts, platformFilter, statusFilter, groupFilter, searchQuery, sortMode]);

  const isFiltered =
    searchQuery.trim() !== '' || statusFilter !== 'all' || platformFilter.length > 0 || groupFilter != null;

  const resetFilters = () => {
    setSearchQuery('');
    setGroupFilter(null);
    setViewPref((prev) => ({ ...prev, filter: 'all', platforms: [] }));
  };

  // A group chosen on a card's "Add to group" select. Errors toast; success
  // is visible on the card itself, which re-renders with the new tag.
  const addToGroup = async (acct: ExternalAccount, value: string) => {
    if (value === '__new__') {
      openManageGroups(null, acct.id);
      return;
    }
    if (value === '__manage__') {
      openManageGroups();
      return;
    }
    const groupId = Number(value);
    if (!groupId) return;
    try {
      const res = await groupMembers.mutateAsync({ id: groupId, add: [acct.id] });
      if (res.inherited.applied > 0 || res.inherited.tier_updated > 0) {
        showSuccess(`Added to ${res.name} — it picked up the group's Flash Buy rule.`);
      }
    } catch (error: any) {
      showError(error?.message || 'Could not add to the group');
    }
  };

  const removeFromGroup = async (acct: ExternalAccount, groupId: number) => {
    try {
      await groupMembers.mutateAsync({ id: groupId, remove: [acct.id] });
    } catch (error: any) {
      showError(error?.message || 'Could not remove from the group');
    }
  };

  const filterOptions: SegmentOption<StatusFilter>[] = STATUS_FILTERS.map((value) => ({
    value,
    label: (
      <>
        {STATUS_LABEL[value]} <span className="text-white/30 tabular-nums">{counts[value]}</span>
      </>
    ),
  }));

  useEffect(() => { trackEvent('accounts_page_viewed'); }, []);

  const connectDiceMutation = useConnectDice();
  const connectTAOMutation = useConnectTAO();
  const disconnectMutation = useDisconnectAccount();
  const syncMutation = useSyncAccount();
  const syncAllMutation = useSyncAllAccounts();
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const openModal = useCallback((platform: ExternalPlatform) => {
    setReconnectAccount(null);
    setModalPlatform(platform);
    setModalOpen(true);
    trackEvent('connect_account_modal_opened', { platform });
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalPlatform(null);
    setReconnectAccount(null);
  }, []);

  const handleConnectDice = async (phone: string, password: string) => {
    try {
      await connectDiceMutation.mutateAsync({ phone, password });
    } catch (error) {
      throw error;
    }
  };

  const handleConnectTAO = async (email: string, password: string, card: string) => {
    try {
      await connectTAOMutation.mutateAsync({ email, password, card });
    } catch (error) {
      throw error;
    }
  };

  const handleDisconnect = async (id: number) => {
    try {
      await disconnectMutation.mutateAsync(id);
      showSuccess(
        t('pages.accounts.disconnectSuccess') ||
          'Account disconnected successfully'
      );
      trackEvent('account_disconnected', { accountId: String(id) });
    } catch (error: any) {
      showError(
        error?.message ||
          t('pages.accounts.disconnectError') ||
          'Failed to disconnect account'
      );
    }
  };

  const handleSync = async (id: number) => {
    setSyncingId(id);
    try {
      const res = await syncMutation.mutateAsync(id);
      if (res?.message) {
        showSuccess(res.message);
      }
      trackEvent('account_synced', { accountId: String(id) });
    } catch (error: any) {
      // SyncFailedError already carries user-safe copy keyed off error_code.
      // The server's own message is written for us, not the seller — the proxy
      // ones have the account's phone number in the middle of the sentence.
      showError(error?.message || 'Failed to sync account');
      trackEvent('account_sync_failed', {
        accountId: String(id),
        errorCode: error instanceof SyncFailedError ? error.errorCode ?? 'none' : 'exception',
      });
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncAll = async () => {
    try {
      const res = await syncAllMutation.mutateAsync(accounts);
      const summary = `${res.succeeded} synced, ${res.failed} failed`;
      if (res.failed === 0) {
        showSuccess(summary);
      } else {
        showError(summary);
      }
      trackEvent('accounts_sync_all', {
        succeeded: String(res.succeeded),
        failed: String(res.failed),
      });
    } catch (error: any) {
      showError(error?.message || 'Failed to sync accounts');
    }
  };

  const reconnectAccountFlow = (acct: ExternalAccount) => {
    setReconnectAccount(acct);
    setModalPlatform(acct.platform);
    setModalOpen(true);
    trackEvent('reconnect_account_modal_opened', { platform: acct.platform });
  };

  // Calculate time since last sync
  const getTimeSince = (dateStr: string | null) => {
    if (!dateStr) return 'Never synced';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <PageShell>
      <PageHeader
        title="Connected Accounts"
        subtitle={
          canManageAccounts
            ? 'Link your ticketing platform accounts to sync inventory'
            : 'The accounts this inventory comes from. Refresh them any time; connecting and disconnecting is the owner\u2019s.'
        }
        actions={
          accounts.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {canManageAccounts && (
                <Button
                  variant="secondary"
                  onClick={() => openManageGroups()}
                  title="Create and edit account groups"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01" />
                    </svg>
                    Groups{groups.length > 0 ? ` (${groups.length})` : ''}
                  </span>
                </Button>
              )}
              <Button
                variant="secondary"
                data-tour="accounts-sync"
                onClick={handleSyncAll}
                disabled={syncAllMutation.isPending || syncMutation.isPending}
                title="Sync all connected accounts"
              >
                <span className="flex items-center justify-center gap-2">
                  <SyncIcon className={cx('w-4 h-4', syncAllMutation.isPending && 'animate-spin')} />
                  {syncAllMutation.isPending
                    ? `Syncing ${syncAllMutation.progress?.done ?? 0}/${syncAllMutation.progress?.total ?? 0}...`
                    : 'Sync all'}
                </span>
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="space-y-6">
        {/* Quick Connect */}
        {canManageAccounts && (
        <Panel
          padding="none"
          tourId="accounts-connect"
          className="bg-gradient-to-br from-white/[0.04] to-white/[0.01]"
        >
          <div className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-medium text-white">Connect a Platform</h2>
              <p className="mt-1 text-sm text-white/40">Add accounts to automatically import tickets</p>
            </div>
            <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap">
              {ALL_PLATFORMS.map((platform) => {
                const config = platformConfig[platform] || defaultPlatformConfig;
                const isConnected = accounts.some(a => a.platform === platform);
                return (
                  <button
                    key={platform}
                    onClick={() => openModal(platform)}
                    className={cx(
                      'group relative rounded-xl border px-5 py-2.5 text-sm font-medium transition-all',
                      'hover:scale-[1.02] active:scale-[0.98]',
                      config.bgColor,
                      config.borderColor,
                      config.color,
                      FOCUS_RING,
                    )}
                  >
                    <span className="flex items-center justify-center gap-2">
                      {platform}
                      {isConnected && (
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </Panel>
        )}

        {/* CrowdVolt connect banner. Hidden while we're not inviting new
            customers into CrowdVolt — the integration itself is untouched, so an
            already-connected account keeps everything it had. */}
        {canManageAccounts && canSeeCvConnect && !isCvConnected && (
          <div className="rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] to-white/[0.01] p-5 transition-all hover:border-violet-500/30">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4 sm:items-center">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-violet-500/20 bg-violet-500/10">
                  <svg className="h-5 w-5 text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-white">Connect your CrowdVolt account</h3>
                  <p className="mt-0.5 text-xs text-white/40">
                    Sync your CrowdVolt listings, mirror prices, and manage cross-platform inventory from one place.
                  </p>
                </div>
              </div>
              <Link
                to={v2Path('/setup')}
                className={cx(
                  'inline-flex flex-shrink-0 items-center justify-center rounded-lg bg-violet-500 px-4 py-2',
                  'text-xs font-semibold text-white transition-all hover:bg-violet-400',
                  FOCUS_RING,
                )}
              >
                Get Started
              </Link>
            </div>
          </div>
        )}

        {/* Toolbar — search first, never inside a disclosure (DESIGN_PLAN §4.4).
            Deliberately the same shape as the Dice accounts toolbar: the two
            screens are both "a long list of numbered accounts", and learning
            one should teach the other. */}
        {accounts.length > 0 && (
          <div className={cx(CARD_SURFACE, 'p-4')}>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                aria-label="Search connected accounts"
                placeholder="Search by number, email or platform..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cx(
                  'w-full rounded-xl border border-white/[0.08] bg-white/[0.03] py-3 pl-11 pr-11 text-sm text-white',
                  'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
                  FOCUS_RING,
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  title="Clear search"
                  className={cx(
                    'absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-white/30',
                    'transition-colors hover:text-white/60',
                    FOCUS_RING,
                  )}
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/[0.06] pt-4">
              <SegmentedControl
                label="Filter accounts by status"
                size="sm"
                options={filterOptions}
                value={statusFilter}
                onChange={setStatusFilter}
              />
              {/* A dropdown, not a second SegmentedControl. Side by side the two
                  read as one set of tabs, but one hides rows and the other only
                  reorders them — so the sort is made visually subordinate
                  instead of competing with the filter. */}
              <label className="flex items-center gap-2">
                <span className="text-xs text-white/40">Sort</span>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as SortMode)}
                  aria-label="Sort accounts"
                  className={cx(
                    'rounded-lg border border-white/[0.10] bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-white/80',
                    'hover:border-white/20 focus:outline-none',
                    FOCUS_RING,
                  )}
                >
                  {SORT_MODES.map((mode) => (
                    <option key={mode} value={mode}>{SORT_LABEL[mode]}</option>
                  ))}
                </select>
              </label>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-xs tabular-nums text-white/40">
                  Showing {visibleAccounts.length} of {accounts.length}
                </span>
                {isFiltered && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className={cx(
                      'rounded-md px-1 py-1 text-xs text-white/40 transition-colors hover:text-white/60',
                      FOCUS_RING,
                    )}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Platform stays multi-select chips rather than joining the status
                SegmentedControl: "DICE and AXS but not TAO" is a normal thing to
                ask for, and a one-of control cannot say it. Hidden when there is
                only one platform to choose from. */}
            {platformChips.length > 1 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-3">
                <span className="text-xs font-medium uppercase tracking-wider text-white/30">
                  Platform
                </span>
                {platformChips.map((platform) => {
                  const isSelected = platformFilter.includes(platform);
                  const count = accounts.filter((a) => a.platform === platform).length;
                  const config = platformConfig[platform] || defaultPlatformConfig;
                  return (
                    <button
                      key={platform}
                      onClick={() => togglePlatform(platform)}
                      aria-pressed={isSelected}
                      className={cx(
                        'rounded-lg border px-3 py-1.5 text-xs font-medium transition-all',
                        isSelected
                          ? cx(config.bgColor, config.borderColor, config.color)
                          : 'border-white/10 bg-white/5 text-white/50 hover:bg-white/10',
                        FOCUS_RING,
                      )}
                    >
                      {platform}
                      <span className={cx('ml-1.5 tabular-nums', isSelected ? 'text-white/60' : 'text-white/30')}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Group chips: one-of, because "show me Brooklyn" is the question
                and an account in two groups already appears under both. */}
            {groups.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-3">
                <span className="text-xs font-medium uppercase tracking-wider text-white/30">
                  Group
                </span>
                {groups.map((g) => {
                  const count = accounts.filter((a) => (a.groups ?? []).some((t) => t.id === g.id)).length;
                  return (
                    <AccountGroupChip
                      key={g.id}
                      group={g}
                      size="md"
                      count={count}
                      active={groupFilter === g.id}
                      onClick={() => setGroupFilter((prev) => (prev === g.id ? null : g.id))}
                    />
                  );
                })}
                {canManageAccounts && (
                  <button
                    type="button"
                    onClick={() => openManageGroups(groupFilter)}
                    className={cx('rounded px-1 py-1 text-xs text-white/40 transition-colors hover:text-white/60', FOCUS_RING)}
                  >
                    Manage
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Accounts List */}
        {loading ? (
          <div role="status" aria-label="Loading accounts..." className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : accountsError ? (
          <EmptyState
            tone="error"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
            title={accountsError instanceof Error ? accountsError.message : 'Failed to load accounts'}
          />
        ) : visibleAccounts.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {visibleAccounts.map((acct) => {
              const config = platformConfig[acct.platform] || defaultPlatformConfig;
              const syncBusy = syncingId === acct.id || syncAllMutation.isPending;
              return (
                <div
                  key={acct.id}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 transition-all hover:border-white/[0.08] hover:bg-white/[0.03] focus-within:border-white/[0.08]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-4">
                      {/* Platform Badge */}
                      <div className={cx('flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border', config.bgColor, config.borderColor)}>
                        <span className={cx('text-sm font-bold', config.color)}>
                          {acct.platform.charAt(0)}
                        </span>
                      </div>

                      {/* Account Info */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-medium text-white">{acct.platform}</h3>
                          <Badge size="sm" tone={acct.is_active ? 'green' : 'neutral'}>
                            {acct.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        {/* The whole number, monospaced so digits line up down
                            the column and a wrong one is visible at a glance.
                            DICE leads with it because a DICE login IS its phone
                            number; every other platform leads with the email.
                            A seat member has no `phone` and falls back to the
                            masked form (see accountIdentity.ts). */}
                        <p className="mt-0.5 break-words text-sm text-white/50">
                          {(() => {
                            const phone = accountPhone(acct);
                            const phoneEl = phone ? (
                              <span className="font-mono text-white/70">{phone}</span>
                            ) : null;
                            const emailEl = acct.email ? <span>{acct.email}</span> : null;
                            const [lead, trail] =
                              acct.platform === 'DICE' ? [phoneEl, emailEl] : [emailEl, phoneEl];
                            if (!lead && !trail) {
                              return <span className="text-white/30">No number or email on file</span>;
                            }
                            return (
                              <>
                                {lead}
                                {lead && trail && <span className="text-white/20"> · </span>}
                                {trail}
                              </>
                            );
                          })()}
                        </p>
                        {/* Cards are stripped from a team member's copy of this
                            response, so the whole block is hidden for them.
                            Rendering it would print "No card on file" — a
                            false statement about the owner's account rather
                            than an accurate one about what the team member can see. */}
                        {acct.platform === 'DICE' && canManageAccounts && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            {acct.saved_cards && acct.saved_cards.length > 0 ? (
                              acct.saved_cards.map((c) => (
                                <Badge key={c.id} title={`${c.brand} ending ${c.last4}`}>
                                  {c.brand} ****{c.last4}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-[11px] text-white/30">No card on file</span>
                            )}
                          </div>
                        )}
                        {/* Group tags. The owner can pull one off here and add
                            one from the select; a team member just reads them. The
                            select is hidden until there is a group to add to
                            AND the account is not already in all of them. */}
                        {(() => {
                          const tags = acct.groups ?? [];
                          const remaining = groups.filter((g) => !tags.some((t) => t.id === g.id));
                          if (tags.length === 0 && !canManageAccounts) return null;
                          return (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {tags.map((g) => (
                                <AccountGroupChip
                                  key={g.id}
                                  group={g}
                                  disabled={groupMembers.isPending}
                                  onRemove={canManageAccounts ? () => removeFromGroup(acct, g.id) : undefined}
                                />
                              ))}
                              {canManageAccounts && (
                                <select
                                  value=""
                                  disabled={groupMembers.isPending}
                                  onChange={(e) => addToGroup(acct, e.target.value)}
                                  aria-label={`Add ${accountPhone(acct) || acct.email || `account ${acct.id}`} to a group`}
                                  className={cx(
                                    'rounded-full border border-dashed border-white/15 bg-transparent px-2 py-0.5 text-[11px] text-white/40',
                                    'hover:border-white/30 hover:text-white/70 focus:outline-none disabled:opacity-50',
                                    FOCUS_RING,
                                  )}
                                >
                                  <option value="" disabled>+ Group</option>
                                  {remaining.map((g) => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                  ))}
                                  <option value="__new__">New group…</option>
                                  {groups.length > 0 && <option value="__manage__">Manage groups…</option>}
                                </select>
                              )}
                            </div>
                          );
                        })()}
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <span className="text-xs text-white/30">
                            Connected {new Date(acct.connected_at).toLocaleDateString()}
                          </span>
                          <span className="text-white/10">·</span>
                          <span className={cx('text-xs', acct.last_synced ? 'text-white/40' : 'text-amber-400/70')}>
                            {getTimeSince(acct.last_synced)}
                          </span>
                          {/* Auth state comes from needs_reconnect, which the server
                              clears the moment a reconnect succeeds. last_sync_*
                              below only describes the last ticket sync. */}
                          {acct.needs_reconnect && (
                            <>
                              <span className="text-white/10">·</span>
                              <Badge tone="red" dot title={acct.is_banned ? 'Account banned' : 'Reconnect needed'}>
                                {acct.is_banned ? 'Account banned' : 'Reconnect needed'}
                              </Badge>
                              {/* Reconnecting is what CLEARS the ban flag —
                                  dice/login.py sets is_banned=False on a
                                  successful login — so hiding this from a
                                  blocked account left the one path back
                                  unreachable if the platform restored it.
                                  Shown for banned accounts too; if the block
                                  still stands the login simply fails again. */}
                              {canManageAccounts && (
                                <button
                                  onClick={() => reconnectAccountFlow(acct)}
                                  className={cx('rounded text-[11px] text-rose-300 underline underline-offset-2 hover:text-rose-200', FOCUS_RING)}
                                >
                                  Reconnect
                                </button>
                              )}
                            </>
                          )}
                          {acct.last_sync_status === 'failed' && !acct.needs_reconnect && (
                            <>
                              <span className="text-white/10">·</span>
                              {/* proxy_blocked and network are our proxy having
                                  a bad minute, not a problem with the account —
                                  amber, and worth retrying, because the backend
                                  has already moved on from the exit nodes it
                                  tried. */}
                              <Badge
                                tone={isRetryableSyncError(acct.last_sync_error_code) ? 'amber' : 'red'}
                                dot
                                title={syncErrorMessage(acct.last_sync_error_code, acct.last_sync_error || 'Sync failed')}
                              >
                                {isRetryableSyncError(acct.last_sync_error_code)
                                  ? 'Temporarily unavailable'
                                  : 'Sync failed'}
                              </Badge>
                              {isRetryableSyncError(acct.last_sync_error_code) && (
                                <button
                                  onClick={() => handleSync(acct.id)}
                                  disabled={syncBusy}
                                  className={cx(
                                    'rounded text-[11px] text-amber-300 underline underline-offset-2 hover:text-amber-200 disabled:opacity-50',
                                    FOCUS_RING,
                                  )}
                                >
                                  Try again
                                </button>
                              )}
                            </>
                          )}
                          {acct.last_sync_status === 'success' && (
                            <>
                              <span className="text-white/10">·</span>
                              <Badge tone="green" dot title="Last sync succeeded">
                                Synced
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/*
                      Actions, `lg`+ — hover-revealed, but resting at 40% rather
                      than 0 and revealed by `focus-within` too, so the
                      affordance is discoverable and keyboard-reachable
                      (DESIGN_PLAN §3.4, §4.4).
                    */}
                    <div className="hidden flex-none items-center gap-2 opacity-40 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 lg:flex">
                      {acct.platform === 'DICE' && canManageAccounts && (
                        <button
                          onClick={() => setAddCardAccount(acct)}
                          className={cx(iconActionClass, 'text-white/60 hover:bg-white/10 hover:text-white')}
                          title="Add card"
                          aria-label="Add card"
                        >
                          <CardIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleSync(acct.id)}
                        disabled={syncBusy}
                        className={cx(iconActionClass, 'text-white/60 hover:bg-white/10 hover:text-white')}
                        title="Sync tickets"
                        aria-label="Sync tickets"
                      >
                        <SyncIcon className={cx('h-4 w-4', syncingId === acct.id && 'animate-spin')} />
                      </button>
                      {canManageAccounts && (
                        <button
                          onClick={() => handleDisconnect(acct.id)}
                          disabled={disconnectMutation.isPending}
                          className={cx(iconActionClass, 'text-white/40 hover:bg-red-500/10 hover:text-red-400')}
                          title="Disconnect"
                          aria-label="Disconnect"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Actions, below `lg` — always visible and labelled. */}
                  <div className="mt-4 flex gap-2 lg:hidden">
                    {acct.platform === 'DICE' && canManageAccounts && (
                      <button
                        onClick={() => setAddCardAccount(acct)}
                        className={cx(labelledActionClass, 'text-white/70 hover:bg-white/10 hover:text-white')}
                        title="Add card"
                        aria-label="Add card"
                      >
                        <CardIcon className="h-4 w-4" />
                        Add card
                      </button>
                    )}
                    <button
                      onClick={() => handleSync(acct.id)}
                      disabled={syncBusy}
                      className={cx(labelledActionClass, 'text-white/70 hover:bg-white/10 hover:text-white')}
                      title="Sync tickets"
                      aria-label="Sync tickets"
                    >
                      <SyncIcon className={cx('h-4 w-4', syncingId === acct.id && 'animate-spin')} />
                      Sync
                    </button>
                    {canManageAccounts && (
                      <button
                        onClick={() => handleDisconnect(acct.id)}
                        disabled={disconnectMutation.isPending}
                        className={cx(labelledActionClass, 'text-white/50 hover:bg-red-500/10 hover:text-red-400')}
                        title="Disconnect"
                        aria-label="Disconnect"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Disconnect
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : accounts.length > 0 ? (
          /* Accounts exist; the toolbar hid them. Saying "No accounts
             connected" here would be a lie that sends the owner off to
             reconnect a platform they never lost. */
          <EmptyState
            icon={<SearchIcon className="h-6 w-6" />}
            title="No accounts match these filters"
            body="Try a different search, status or platform."
            action={
              <Button variant="secondary" onClick={resetFilters}>
                Reset filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            }
            title="No accounts connected"
            body={
              canManageAccounts
                ? 'Connect your ticketing platform accounts above to start syncing your inventory'
                : 'The account owner has not connected a platform yet, so there is no inventory to work with.'
            }
          />
        )}
      </div>

      {/* Modal */}
      <ConnectAccountModal
        open={modalOpen}
        platform={modalPlatform}
        onClose={closeModal}
        onConnectDice={handleConnectDice}
        onConnectTAO={handleConnectTAO}
        reconnectAccount={reconnectAccount}
      />

      <AddCardModal
        open={addCardAccount !== null}
        accountId={addCardAccount?.id ?? null}
        accountLabel={(addCardAccount && accountPhone(addCardAccount)) || addCardAccount?.email}
        onClose={() => setAddCardAccount(null)}
        onAdded={() => { refetchAccounts(); }}
      />

      {canManageAccounts && (
        <ManageGroupsModal
          open={manageGroups.open}
          onClose={() => setManageGroups((prev) => ({ ...prev, open: false }))}
          accounts={accounts}
          groups={groups}
          initialGroupId={manageGroups.groupId}
          initialAccountId={manageGroups.accountId}
        />
      )}
    </PageShell>
  );
};
